/**
 * Landing pubblica pre-signup.
 *
 * Mostrata ai visitatori non autenticati. Gli utenti già loggati vengono
 * dirottati su /dashboard. Sezioni:
 *  - Hero con CTA "Inizia trial 7 giorni gratis"
 *  - Per chi è (cartongessisti, studi tecnici, capisquadra)
 *  - Features in 4 card (configuratore, listini multi-fornitore, preventivi, CRM)
 *  - Pricing 4 piani (Trial, Solo, Studio, Team)
 *  - "Come funziona" 3 step
 *  - FAQ corta
 *  - Footer minimale
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Layers, Package, FileText, Users, CheckCircle2, ArrowRight, Sparkles,
  Calendar, MapPin, TrendingUp, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/brand/Logo';

interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  users: string;
  cta: string;
  highlight?: boolean;
  features: string[];
  comingSoon?: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'trial',
    name: 'Trial',
    price: 'Gratis',
    period: '7 giorni',
    users: '1 utente',
    cta: 'Inizia gratis',
    features: [
      'Tutto incluso, niente carta',
      'Configuratore pareti & controsoffitti',
      'Catalogo Saint-Gobain incluso',
      'Preventivi illimitati',
      'Mappa clienti & dashboard',
    ],
  },
  {
    id: 'solo',
    name: 'Solo',
    price: '€19',
    period: '/mese',
    users: '1 utente',
    cta: 'Scegli Solo',
    features: [
      'Progetti & preventivi illimitati',
      'Listini multi-fornitore',
      'Sconti e mark-up personalizzati',
      'Export PDF preventivi & RDA',
      'Mappa clienti',
    ],
  },
  {
    id: 'studio',
    name: 'Studio',
    price: '€49',
    period: '/mese',
    users: '1 utente',
    cta: 'Scegli Studio',
    highlight: true,
    features: [
      'Tutto del piano Solo, più:',
      'Vista 3D & scheda tecnica',
      'Report "Ordine al fornitore"',
      'CRM clienti & agenda',
      'History prezzo materiali',
      'Supporto email prioritario',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    price: '€149',
    period: '/mese',
    users: 'fino a 10 utenti',
    cta: 'Scegli Team',
    features: [
      'Tutto di Studio, più:',
      'Multi-utente con ruoli granulari',
      'Inviti via email',
      'Audit log',
      'Supporto telefonico',
    ],
  },
];

const FEATURES = [
  {
    icon: <Layers className="h-6 w-6" />,
    title: 'Configuratore parete & controsoffitto',
    description: 'Componi stratigrafie layer per layer, parti da composizioni certificate Saint-Gobain o Knauf e personalizzale.',
  },
  {
    icon: <Package className="h-6 w-6" />,
    title: 'Listini multi-fornitore',
    description: 'Importa i listini Excel di Saint-Gobain, Knauf, Fassa Bortolo. Sconti famiglia + extra applicati automaticamente.',
  },
  {
    icon: <FileText className="h-6 w-6" />,
    title: 'Preventivi pro & PDF',
    description: 'Genera preventivi sintetici e completi. IVA multipla per riga, mark-up override, sconto cliente. Workflow bozza → inviato → vinto/perso.',
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: 'CRM & agenda',
    description: 'Storico clienti, attività, chiamate e follow-up. Mappa geografica con fatturato per pin. Win-rate e margine medio per cliente.',
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: 'Dashboard commerciale',
    description: 'KPI vinti/pipeline/conversion. Top clienti, top stratigrafie, alert offerte in scadenza. Filtri periodo + drill-down per ogni metrica.',
  },
  {
    icon: <MapPin className="h-6 w-6" />,
    title: 'Mappa clienti',
    description: 'Pin geografici proporzionali al fatturato. Filtra offerte/vinti/pipeline per vedere dove vendi e dove chiudi davvero.',
  },
];

const Index = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* HEADER PUBBLICO */}
      <header className="border-b sticky top-0 z-40 bg-background/90 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={32} />
            <span className="font-bold text-lg tracking-tight">DryConfig</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/auth')}>Accedi</Button>
            <Button onClick={() => navigate('/auth')}>Inizia gratis</Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="container mx-auto px-4 py-20 text-center">
        <Badge variant="outline" className="mb-6 gap-1.5 py-1 px-3">
          <Sparkles className="h-3 w-3" />
          Per cartongessisti, studi tecnici, capisquadra
        </Badge>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto leading-tight">
          Dal sopralluogo al preventivo. <br />
          <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            In meno di 5 minuti.
          </span>
        </h1>
        <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
          Configura stratigrafie a secco, applica i listini dei fornitori con i tuoi sconti,
          genera preventivi professionali in PDF. Tutto in un'unica app italiana.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button size="lg" onClick={() => navigate('/auth')} className="gap-2 h-12 px-6 text-base">
            Inizia trial 7 giorni gratis
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => navigate('/auth')} className="h-12 px-6 text-base">
            Accedi
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Senza carta di credito · Cancelli quando vuoi · Dati al sicuro su server EU
        </p>
      </section>

      {/* FEATURES GRID */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight mb-3">Tutto quello che ti serve</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Sostituisce 4-5 strumenti diversi (Excel listini, Word per preventivi, CRM, AutoCAD per le stratigrafie) con un'unica app pensata per il cartongesso.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {FEATURES.map((f, i) => (
            <Card key={i} className="border-primary/10 hover:border-primary/30 transition">
              <CardHeader>
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
                  {f.icon}
                </div>
                <CardTitle className="text-base">{f.title}</CardTitle>
                <CardDescription className="text-sm">{f.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-3">Come funziona</h2>
            <p className="text-muted-foreground">Tre passi per il tuo primo preventivo.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { n: '1', title: 'Importa il listino del tuo fornitore', desc: 'Excel di Saint-Gobain, Knauf, Fassa Bortolo. Sconti e prezzi netti applicati automaticamente.' },
              { n: '2', title: 'Configura la stratigrafia', desc: 'Parti da una composizione certificata o costruisci layer per layer. Le viti, gli sfridi, la finitura: tutto calcolato.' },
              { n: '3', title: 'Genera il preventivo', desc: 'Aggiungi le pareti al preventivo, applica mark-up e IVA, esporta il PDF al cliente. Workflow di vendita integrato.' },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                  {s.n}
                </div>
                <h3 className="font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight mb-3">Piani e prezzi</h2>
          <p className="text-muted-foreground">Inizia gratis per 7 giorni. Cancella quando vuoi.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {PLANS.map((p) => (
            <Card
              key={p.id}
              className={`relative flex flex-col ${
                p.highlight ? 'border-primary ring-2 ring-primary/20 shadow-lg' : ''
              }`}
            >
              {p.highlight && (
                <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  Più popolare
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="text-xl">{p.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">{p.price}</span>
                  <span className="text-sm text-muted-foreground"> {p.period}</span>
                </div>
                <CardDescription className="text-xs">{p.users}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <ul className="space-y-2 flex-1 text-sm">
                  {p.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-6"
                  variant={p.highlight ? 'default' : 'outline'}
                  onClick={() => navigate('/auth')}
                >
                  {p.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center mt-6">
          Prezzi IVA esclusa · Fatturazione mensile · Cambio piano in qualunque momento
        </p>
      </section>

      {/* TRUST */}
      <section className="border-t bg-muted/20 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
            <div>
              <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold text-sm">Dati su server EU</p>
              <p className="text-xs text-muted-foreground">GDPR compliant</p>
            </div>
            <div>
              <Calendar className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold text-sm">7 giorni gratis</p>
              <p className="text-xs text-muted-foreground">Nessuna carta richiesta</p>
            </div>
            <div>
              <Package className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold text-sm">1100+ materiali</p>
              <p className="text-xs text-muted-foreground">Catalogo Saint-Gobain incluso</p>
            </div>
            <div>
              <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-semibold text-sm">Multi-utente</p>
              <p className="text-xs text-muted-foreground">Ruoli granulari (piano Team)</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
          Pronto per il tuo primo preventivo?
        </h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
          Bastano 5 minuti per creare la tua organizzazione e iniziare. Niente carta, niente call commerciali.
        </p>
        <Button size="lg" onClick={() => navigate('/auth')} className="gap-2 h-12 px-8 text-base">
          Inizia trial 7 giorni gratis
          <ArrowRight className="h-4 w-4" />
        </Button>
      </section>

      {/* FOOTER */}
      <footer className="border-t bg-muted/30 py-8">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Logo size={20} />
            <span className="font-semibold">DryConfig</span>
          </div>
          <p>
            Software professionale per la preventivazione di pareti a secco e controsoffitti.
          </p>
          <p className="mt-1">
            Sviluppato da Fabrizio Tacchino · © {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
